import {Button, Input, Table, Typography, Space, DatePicker, message} from "antd";
import {DownOutlined, EditOutlined, PlusOutlined, SaveOutlined, UpOutlined} from "@ant-design/icons";
import React, {useEffect, useState} from "react";
import dayjs from "dayjs";
import {HttpService} from "../../../services/http/Http.service";
import {UtilsService} from "../../../services/Utils.service";
import {HttpErrorService} from "../../../services/http/HttpError.service";

const {Title} = Typography;

export default function VariablesManagement() {
    const [adminExpData, setAdminExpData] = useState<any[]>([]);
    const [editingKey, setEditingKey] = useState<string | null>(null);
    const [interestData, setInterestData] = useState<any[]>([]);
    const [editingInterestKey, setEditingInterestKey] = useState<string | null>(null);
    const [variabileData, setVariabileData] = useState<any[]>([]);
    const [editingVariabileKey, setEditingVariabileKey] = useState<string | null>(null);
    const [rateizzazioneData, setRateizzazioneData] = useState<any[]>([]);
    const [editingRateizzazioneKey, setEditingRateizzazioneKey] = useState<string | null>(null);
    const [isInteressiExpanded, setIsInteressiExpanded] = useState(false);
    const [isPianoRateizzazioneExpanded, setIsPianoRateizzazioneExpanded] = useState(false);


    const fetchSpesePratica = async () => {
        try {
            const response = await HttpService.getInstanceAxios().get("/spesePratica/list");
            const data = response.data.map((item: any) => ({
                key: item.id.toString(),
                id: item.id,
                type: item.type,
                value: item.amount,
                updatedAt: item.updatedAt,
                updatedBy: item.updatedBy,
            }));
            setAdminExpData(data);
        } catch (error) {
            message.error("Errore durante il recupero delle spese.");
        }
    };

    const fetchInteressePratica = async () => {
        try {
            const response = await HttpService.getInstanceAxios().get("/interessePratica/list");
            const data = response.data.map((item: any) => ({
                key: item.id.toString(),
                id: item.id,
                from: item.startDate,
                to: item.endDate,
                value: item.value,
                norma: item.norma,
                updatedAt: item.updatedAt,
                updatedBy: item.updatedBy,
                canEdit: item.canEdit,
            }));
            setInterestData(data);
        } catch (error) {
            message.error("Errore durante il recupero degli interessi.");
        }
    };

    const fetchVariabiliPratica = async () => {
        try {
            const response = await HttpService.getInstanceAxios().get("/variabilePratica/list");
            const data = response.data.map((item: any) => ({
                key: item.id.toString(),
                id: item.id,
                type: item.type,
                label: item.label,
                numberOfDays: item.numberOfDays,
                updatedAt: item.updatedAt,
                updatedBy: item.updatedBy,
            }));
            setVariabileData(data);
        } catch (error) {
            message.error("Errore durante il recupero delle variabili pratica.");
        }
    };

    const fetchRateizzazionePratica = async () => {
        try {
            const response = await HttpService.getInstanceAxios().get("/rateizzazionePratica/list");
            const data = response.data.map((item: any) => ({
                key: item.id.toString(),
                id: item.id,
                amountFrom: item.amountFrom,
                amountTo: item.amountTo,
                installmentNumber: item.installmentNumber,
                updatedAt: item.updatedAt,
                updatedBy: item.updatedBy
            }));
            setRateizzazioneData(data);
        } catch (error) {
            message.error("Errore durante il recupero del piano di rateizzazione.");
        }
    };

    useEffect(() => {
        fetchSpesePratica();
        fetchInteressePratica();
        fetchVariabiliPratica();
        fetchRateizzazionePratica();
    }, []);

    const handleEditClick = (key: string) => setEditingKey(key);
    const handleValueChange = (value: number, key: string) => {
        setAdminExpData(prev =>
            prev.map(item => (item.key === key ? {...item, value} : item))
        );
    };

    const handleSaveClick = async (item: any) => {
        try {
            await HttpService.getInstanceAxios().post(`/spesePratica/update/${item.id}`, null, {
                params: {amount: item.value},
            });
            message.success("Importo aggiornato con successo.");
            setEditingKey(null);
            fetchSpesePratica();
        } catch (error) {
            message.error("Errore durante il salvataggio dell'importo.");
        }
    };

    const handleEditVariabileClick = (key: string) => setEditingVariabileKey(key);
    const handleVariabileValueChange = (newValue: number, key: string) => {
        setVariabileData(prev =>
            prev.map(item => (item.key === key ? {...item, numberOfDays: newValue} : item))
        );
    };

    const handleSaveVariabileClick = async (item: any) => {
        try {
            await HttpService.getInstanceAxios().post(`/variabilePratica/update/${item.id}`, null, {
                params: {numberOfDays: item.numberOfDays},
            });
            message.success("Variabile aggiornata con successo.");
            setEditingVariabileKey(null);
            fetchVariabiliPratica();
        } catch (error) {
            HttpErrorService.handle("Errore durante il salvataggio della variabile", error, true)
        }
    };

    const handleInterestEditClick = (key: string) => setEditingInterestKey(key);
    const handleInterestCancelClick = (key: string) => {
        const isNewRow = interestData.find(item => item.key === key)?.isNew;
        if (isNewRow) {
            setInterestData(prev => prev.filter(item => item.key !== key));
        }
        setEditingInterestKey(null);
    };

    const handleInterestChange = (key: string, field: string, value: any) => {
        const updated = interestData.map(item =>
            item.key === key ? {...item, [field]: value} : item
        );
        setInterestData(updated);
    };

    const handleRateizzazioneEditClick = (key: string) => setEditingRateizzazioneKey(key);
    const handleRateizzazioneCancelClick = (key: string) => {
        const isNewRow = rateizzazioneData.find(item => item.key === key)?.isNew;
        if (isNewRow) {
            setRateizzazioneData(prev => prev.filter(item => item.key !== key));
        }
        setEditingRateizzazioneKey(null);
    };

    const handleRateizzazioneChange = (key: string, field: string, value: any) => {
        const updated = rateizzazioneData.map(item =>
            item.key === key ? {...item, [field]: value} : item
        );
        setRateizzazioneData(updated);
    };

    const handleSaveRateizzazioneClick = async (item: any) => {
        try {
            await HttpService.getInstanceAxios().post(`/rateizzazionePratica/update/${item.id}`, null, {
                params: {
                    amountFrom: item.amountFrom,
                    amountTo: item.amountTo,
                    installmentNumber: item.installmentNumber
                },
            });
            message.success("Scaglione rateizzazione aggiornato con successo.");
            setEditingRateizzazioneKey(null);
            fetchRateizzazionePratica();
        } catch (error) {
            message.error("Errore durante il salvataggio dello scaglione rateizzazione.");
        }
    };

    const intervalsOverlap = (fromA: string, toA: string, fromB: string, toB: string): boolean => {
        return dayjs(fromA).isBefore(toB) && dayjs(toA).isAfter(fromB);
    };

    const handleInterestSaveClick = async (record: any) => {
        const overlaps = interestData.some(item =>
            item.key !== record.key &&
            intervalsOverlap(record.from, record.to, item.from, item.to)
        );

        if (overlaps) {
            message.error("Le date si sovrappongono a un intervallo esistente.");
            return;
        }

        try {
            if (record.isNew) {
                await HttpService.getInstanceAxios().post(`/interessePratica/create`, null, {
                    params: {
                        startDate: record.from,
                        endDate: record.to,
                        value: record.value,
                        norma: record.norma,
                    }
                });
                message.success("Interesse creato con successo.");
            } else {
                await HttpService.getInstanceAxios().post(`/interessePratica/update/${record.id}`, null, {
                    params: {
                        startDate: record.from,
                        endDate: record.to,
                        value: record.value,
                        norma: record.norma,
                    }
                });
                message.success("Interesse aggiornato con successo.");
            }
            setEditingInterestKey(null);
            fetchInteressePratica();
        } catch (error) {
            message.error("Errore durante il salvataggio dell'interesse.");
        }
    };

    const addInterestRow = () => {
        const sorted = [...interestData].sort((a, b) =>
            dayjs(b.to).diff(dayjs(a.to))
        );

        const lastEnd = sorted[0]?.to ? dayjs(sorted[0].to) : dayjs();
        const newStart = lastEnd.add(1, 'day');
        const newEnd = dayjs(`${newStart.year()}-12-31`);

        const newRow = {
            key: `new-${Date.now()}`,
            from: newStart.format("YYYY-MM-DD"),
            to: newEnd.format("YYYY-MM-DD"),
            value: 0,
            norma: "",
            isNew: true,
            canEdit: true,
        };
        setInterestData([...interestData, newRow]);
        setEditingInterestKey(newRow.key);
    };

    const administrativeExpenses = [
        {
            title: "Tipo",
            dataIndex: "type",
            key: "type",
        },
        {
            title: "Importo",
            dataIndex: "value",
            key: "value",
            render: (_: any, record: any) =>
                editingKey === record.key ? (
                    <Input
                        type="number"
                        value={record.value}
                        onChange={(e) => handleValueChange(Number(e.target.value), record.key)}
                        style={{width: 100}}
                        min={1}
                    />
                ) : (
                    <span>€ {UtilsService.formatNumber(record.value)}</span>
                ),
        },
        {
            title: "Ultimo aggiornamento",
            dataIndex: "updatedAt",
            key: "updatedAt",
            render: (value: string) =>
                value ? dayjs(value).format("DD/MM/YYYY HH:mm") : "—",
        },
        {
            title: "Aggiornato da",
            dataIndex: "updatedBy",
            key: "updatedBy",
            render: (user: any) =>
                user ? `${user.name} ${user.lastname}` : "—",
        },
        {
            title: "Azioni",
            key: "actions",
            render: (_: any, record: any) =>
                editingKey === record.key ? (
                    <Button
                        icon={<SaveOutlined/>}
                        type="primary"
                        onClick={() => handleSaveClick(record)}
                    />
                ) : (
                    <Button
                        icon={<EditOutlined/>}
                        type="primary"
                        onClick={() => handleEditClick(record.key)}
                    />
                ),
        }
    ];

    const administrativeVariables = [
        {
            title: "Label",
            dataIndex: "label",
            key: "label",
        },
        {
            title: "Giorni / Importo",
            dataIndex: "numberOfDays",
            key: "numberOfDays",
            render: (_: any, record: any) => {
                const valueToDisplay = record.numberOfDays;

                return editingVariabileKey === record.key ? (
                    <Input
                        min={1}
                        type="number"
                        value={valueToDisplay}
                        onChange={(e) => handleVariabileValueChange(Number(e.target.value), record.key)}
                        style={{width: 100}}
                    />
                ) : (
                    <span>{typeof valueToDisplay === 'number' ? valueToDisplay.toFixed(0) : ''}</span>
                );
            },
        },
        {
            title: "Ultimo aggiornamento",
            dataIndex: "updatedAt",
            key: "updatedAt",
            render: (value: string) =>
                value ? dayjs(value).format("DD/MM/YYYY HH:mm") : "—",
        },
        {
            title: "Aggiornato da",
            dataIndex: "updatedBy",
            key: "updatedBy",
            render: (user: any) =>
                user ? `${user.name} ${user.lastname}` : "—",
        },
        {
            title: "Azioni",
            key: "actions",
            render: (_: any, record: any) =>
                editingVariabileKey === record.key ? (
                    <Button
                        icon={<SaveOutlined/>}
                        type="primary"
                        onClick={() => handleSaveVariabileClick(record)}
                    />
                ) : (
                    <Button
                        icon={<EditOutlined/>}
                        type="primary"
                        onClick={() => handleEditVariabileClick(record.key)}
                    />
                ),
        }
    ];

    const interestsColumns = [
        {
            title: "Dal",
            dataIndex: "from",
            key: "from",
            render: (_: any, record: any) =>
                editingInterestKey === record.key ? (
                    <DatePicker
                        format="DD/MM/YYYY"
                        value={record.from ? dayjs(record.from) : null}
                        onChange={(date) =>
                            handleInterestChange(record.key, "from", date ? date.format("YYYY-MM-DD") : "")
                        }
                    />
                ) : (
                    dayjs(record.from).format("DD/MM/YYYY")
                ),
        },
        {
            title: "Al",
            dataIndex: "to",
            key: "to",
            render: (_: any, record: any) =>
                editingInterestKey === record.key ? (
                    <DatePicker
                        format="DD/MM/YYYY"
                        value={record.to ? dayjs(record.to) : null}
                        onChange={(date) =>
                            handleInterestChange(record.key, "to", date ? date.format("YYYY-MM-DD") : "")
                        }
                    />
                ) : (
                    dayjs(record.to).format("DD/MM/YYYY")
                ),
        },
        {
            title: "Saggio",
            dataIndex: "value",
            key: "value",
            render: (_: any, record: any) =>
                editingInterestKey === record.key ? (
                    <Input
                        min={0}
                        type="number"
                        value={record.value}
                        onChange={(e) =>
                            handleInterestChange(record.key, "value", e.target.value)
                        }
                    />
                ) : (
                    `${UtilsService.formatNumber(record.value)}%`
                ),
        },
        {
            title: "Norma",
            dataIndex: "norma",
            key: "norma",
            render: (_: any, record: any) =>
                editingInterestKey === record.key ? (
                    <Input
                        value={record.norma}
                        onChange={(e) =>
                            handleInterestChange(record.key, "norma", e.target.value)
                        }
                    />
                ) : (
                    record.norma || "—"
                )
        },
        {
            title: "Ultimo aggiornamento",
            dataIndex: "updatedAt",
            key: "updatedAt",
            render: (value: string) =>
                value ? dayjs(value).format("DD/MM/YYYY HH:mm") : "—",
        },
        {
            title: "Aggiornato da",
            dataIndex: "updatedBy",
            key: "updatedBy",
            render: (user: any) =>
                user ? `${user.name} ${user.lastname}` : "—",
        },
        {
            title: "Azioni",
            key: "actions",
            render: (_: any, record: any, index: number) => {
                const isLastRow = index === interestData.length - 1;

                if (!record.canEdit || !isLastRow) return null;

                return editingInterestKey === record.key ? (
                    <Space>
                        <Button
                            icon={<SaveOutlined/>}
                            type="primary"
                            onClick={() => handleInterestSaveClick(record)}
                        />
                        <Button danger onClick={() => handleInterestCancelClick(record.key)}>
                            Annulla
                        </Button>
                    </Space>
                ) : (
                    <Button
                        icon={<EditOutlined/>}
                        type="primary"
                        onClick={() => handleInterestEditClick(record.key)}
                    />
                );
            }
        }

    ];

    const rateizzazioneColumns = [
        {
            title: "Importo Da",
            dataIndex: "amountFrom",
            key: "amountFrom",
            render: (_: any, record: any) => {
                const valueToDisplay = record.amountFrom;

                return editingRateizzazioneKey === record.key ? (
                    <Input
                        min={1}
                        type="number"
                        value={valueToDisplay}
                        onChange={(e) => handleRateizzazioneChange(record.key, "amountFrom", e.target.value)}
                        style={{width: 100}}
                    />
                ) : (
                    <span>{typeof valueToDisplay === 'number' ? `€ ${UtilsService.formatNumber(valueToDisplay)}` : ''}</span>
                );
            },
        },
        {
            title: "Importo A",
            dataIndex: "amountTo",
            key: "amountTo",
            render: (_: any, record: any) => {
                const valueToDisplay = record.amountTo;

                return editingRateizzazioneKey === record.key ? (
                    <Input
                        min={1}
                        type="number"
                        value={valueToDisplay}
                        onChange={(e) => handleRateizzazioneChange(record.key, "amountTo", e.target.value)}
                        style={{width: 100}}
                    />
                ) : (
                    <span>{typeof valueToDisplay === 'number' ? `€ ${UtilsService.formatNumber(valueToDisplay)}` : ''}</span>
                );
            },
        },
        {
            title: "Numero rate",
            dataIndex: "installmentNumber",
            key: "installmentNumber",
            render: (_: any, record: any) => {
                const valueToDisplay = record.installmentNumber;

                return editingRateizzazioneKey === record.key ? (
                    <Input
                        min={1}
                        type="number"
                        value={valueToDisplay}
                        onChange={(e) => handleRateizzazioneChange(record.key, "installmentNumber", e.target.value)}
                        style={{width: 100}}
                    />
                ) : (
                    <span>{typeof valueToDisplay === 'number' ? valueToDisplay.toFixed(0) : ''}</span>
                );
            },
        },
        {
            title: "Ultimo aggiornamento",
            dataIndex: "updatedAt",
            key: "updatedAt",
            render: (value: string) =>
                value ? dayjs(value).format("DD/MM/YYYY HH:mm") : "—",
        },
        {
            title: "Aggiornato da",
            dataIndex: "updatedBy",
            key: "updatedBy",
            render: (user: any) =>
                user ? `${user.name} ${user.lastname}` : "—",
        },
        {
            title: "Azioni",
            key: "actions",
            render: (_: any, record: any) => {

                return editingRateizzazioneKey === record.key ? (
                    <Space>
                        <Button
                            icon={<SaveOutlined/>}
                            type="primary"
                            onClick={() => handleSaveRateizzazioneClick(record)}
                        />
                        <Button danger onClick={() => handleRateizzazioneCancelClick(record.key)}>
                            Annulla
                        </Button>
                    </Space>
                ) : (
                    <Button
                        icon={<EditOutlined/>}
                        type="primary"
                        onClick={() => handleRateizzazioneEditClick(record.key)}
                    />
                );
            }
        }
    ];

    return (
        <>
            <Space direction={"vertical"} style={{display: 'flex'}}>
                <Title level={3}>Spese amministrative</Title>
                <Table
                    columns={administrativeExpenses}
                    dataSource={adminExpData}
                    pagination={false}
                    rowKey="key"
                />

                <div
                    onClick={() => setIsInteressiExpanded(!isInteressiExpanded)}
                    className={"expand-icon"}
                    style={{marginTop: 20}}
                >
                    <Title level={3} style={{margin: 0}}>Interessi</Title>
                    {isInteressiExpanded ? (
                        <UpOutlined className={"expand-icon-size"}/>
                    ) : (
                        <DownOutlined className={"expand-icon-size"}/>
                    )}
                </div>

                {isInteressiExpanded &&
                    <>
                        <Table
                            columns={interestsColumns}
                            dataSource={interestData}
                            pagination={false}
                            rowKey="key"
                        />
                        <div style={{display: "flex", justifyContent: "flex-end"}}>
                            <Space style={{marginTop: 16}}>
                                <Button icon={<PlusOutlined/>} onClick={addInterestRow}>
                                    Aggiungi interessi
                                </Button>
                            </Space>
                        </div>
                    </>
                }

                <Title level={3}>Variabili amministrative</Title>
                <Table
                    columns={administrativeVariables}
                    dataSource={variabileData}
                    pagination={false}
                    rowKey="key"
                />

                <div
                    onClick={() => setIsPianoRateizzazioneExpanded(!isPianoRateizzazioneExpanded)}
                    className={"expand-icon"}
                    style={{marginTop: 20}}
                >
                    <Title level={3} style={{margin: 0}}>Piano di rateizzazione</Title>
                    {isPianoRateizzazioneExpanded ? (
                        <UpOutlined className={"expand-icon-size"}/>
                    ) : (
                        <DownOutlined className={"expand-icon-size"}/>
                    )}
                </div>

                {isPianoRateizzazioneExpanded &&
                    <Table
                        columns={rateizzazioneColumns}
                        dataSource={rateizzazioneData}
                        pagination={false}
                        rowKey="key"
                    />
                }

                <Title level={3} style={{marginTop: 40}}>Sanzioni amministrative</Title>
                <Table></Table>
            </Space>
        </>
    );
}
